// The one place an episode lives in the browser while it is being worked on. The editor shows it and changes it, and
// work that finishes later (a voice, a painted picture, an animation) changes it here too, whether the editor is open
// or not. So there is only ever one copy, and nothing has to guess where the episode is.
//
// - Every change goes through `update`, which keeps undo and redo.
// - The store saves the episode itself, 0.4 s after the last change, one save at a time. Each save says which saved
//   version it started from (`rev`). If the file changed since (another tab saved it), the server refuses, and the
//   store stops saving that episode and says so, rather than writing over the other tab's work.
// - It lives on globalThis, so when the app reloads its code (each time new work is merged in) the episode, its undo
//   history and any work still running keep going.
import type { TimelineEpisode } from "../../model/timeline";
import { isTimelineEpisode } from "../../model/timeline";
import { api } from "../api";

export type Update<T = TimelineEpisode> = (fn: (e: T) => T, opts?: { merge?: string; quiet?: boolean }) => void;
export type EpisodeView = { ep: TimelineEpisode; canUndo: boolean; canRedo: boolean; saveError: string | null };

type Entry = {
  past: TimelineEpisode[]; present: TimelineEpisode; future: TimelineEpisode[];
  lastMerge: { key: string; at: number } | null;
  rev: number; // the saved version this copy was loaded from or last saved as
  dirty: boolean; // changed since the last save started
  saving: Promise<void> | null;
  timer: ReturnType<typeof setTimeout> | null;
  saveError: string | null;
  stopped: boolean; // another tab saved this episode: stop writing over it
  view: EpisodeView;
  listeners: Set<() => void>;
};

const LIMIT = 100;
const SAVE_AFTER = 400;
const g = globalThis as { sketchyEpisodeStore?: { entries: Map<string, Entry>; loading: Map<string, Promise<Entry>> } };
const store = (g.sketchyEpisodeStore ??= { entries: new Map(), loading: new Map() });

const viewOf = (e: Entry): EpisodeView => ({ ep: e.present, canUndo: e.past.length > 0, canRedo: e.future.length > 0, saveError: e.saveError });
function changed(e: Entry) {
  e.view = viewOf(e);
  for (const l of e.listeners) l();
}

// A line stuck in "syncing" when the page was loaded was cut off by a reload: it becomes an error, never a fake mouth.
export const settleInterrupted = (ep: TimelineEpisode): TimelineEpisode => ({
  ...ep,
  lines: ep.lines.map(l => (l.how !== "silent" && l.status === "syncing" ? { ...l, status: "error" as const, error: "Interrupted while it was being made. Make this line again." } : l)),
});

function newEntry(ep: TimelineEpisode): Entry {
  const e: Entry = {
    past: [], present: ep, future: [], lastMerge: null, rev: ep.rev ?? 0, dirty: false, saving: null, timer: null,
    saveError: null, stopped: false, view: null!, listeners: new Set(),
  };
  e.view = viewOf(e);
  return e;
}

// The episode `id`, loaded from the server the first time. Opening it again later reloads it if it has no unsaved
// changes and another tab has saved a newer copy since. Resolves to "old" for an episode made with the old editor.
export async function openEpisode(id: string): Promise<EpisodeView | "old"> {
  const have = store.entries.get(id);
  if (have) {
    if (!have.dirty && !have.saving && !have.timer) {
      const saved = await api.getEpisode(id);
      if (isTimelineEpisode(saved) && (saved.rev ?? 0) !== have.rev) {
        Object.assign(have, { past: [], future: [], present: saved, rev: saved.rev ?? 0, stopped: false, saveError: null });
        changed(have);
      }
    }
    return have.view;
  }
  const saved = await api.getEpisode(id);
  if (!isTimelineEpisode(saved)) return "old";
  return (await entryFor(id, saved)).view;
}

// The entry for `id`, loading it if this tab hasn't yet. Two callers at once share one load.
async function entryFor(id: string, fetched?: TimelineEpisode): Promise<Entry> {
  const have = store.entries.get(id);
  if (have) return have;
  let p = store.loading.get(id);
  if (!p) {
    p = (async () => {
      const saved = fetched ?? await api.getEpisode(id);
      if (!isTimelineEpisode(saved)) throw new Error("This episode was made with the old editor.");
      const e = store.entries.get(id) ?? newEntry(settleInterrupted(saved));
      store.entries.set(id, e);
      return e;
    })();
    store.loading.set(id, p);
    void p.catch(() => {}).finally(() => store.loading.delete(id));
  }
  return p;
}

function apply(e: Entry, fn: (ep: TimelineEpisode) => TimelineEpisode, opts: { merge?: string; quiet?: boolean } = {}) {
  const next = fn(e.present);
  if (next === e.present) return;
  if (opts.quiet) {
    // A finished voice applies to every kept version too, so undo never brings back a half-finished recording.
    const each = (v: TimelineEpisode) => { try { return fn(v); } catch { return v; } };
    e.past = e.past.map(each);
    e.future = e.future.map(each);
  } else {
    const now = Date.now();
    const merging = opts.merge && e.lastMerge?.key === opts.merge && now - e.lastMerge.at < 1500;
    e.lastMerge = opts.merge ? { key: opts.merge, at: now } : null;
    if (!merging) e.past = [...e.past, e.present].slice(-LIMIT);
    e.future = [];
  }
  e.present = next;
  touched(e);
}

function touched(e: Entry) {
  e.dirty = true;
  changed(e);
  if (e.timer) clearTimeout(e.timer);
  e.timer = setTimeout(() => { e.timer = null; void save(e); }, SAVE_AFTER);
}

// Writes the newest copy. One save at a time: a change made while a save is going is written by the next one.
function save(e: Entry): Promise<void> {
  if (e.stopped) return Promise.resolve();
  if (e.saving) return e.saving.then(() => (e.dirty ? save(e) : undefined));
  e.dirty = false;
  const ep = e.present;
  e.saving = (async () => {
    try {
      const r = await api.save(ep, e.rev);
      e.rev = r.rev;
      if (e.saveError) { e.saveError = null; changed(e); }
    } catch (err) {
      e.dirty = true;
      if ((err as { status?: number }).status === 409) {
        e.stopped = true;
        e.saveError = "This episode was changed in another tab, so changes here aren't being saved. Reload the page to carry on.";
      } else {
        e.saveError = `Couldn't save: ${(err as Error).message}`;
        // Try again shortly; the next change will also try.
        if (!e.timer) e.timer = setTimeout(() => { e.timer = null; void save(e); }, 3000);
      }
      changed(e);
    } finally {
      e.saving = null;
    }
  })();
  return e.saving.then(() => (e.dirty && !e.stopped && !e.timer ? save(e) : undefined));
}

// Writes any change still waiting for its autosave now (the editor closing).
export function saveNow(id: string): Promise<void> {
  const e = store.entries.get(id);
  if (!e) return Promise.resolve();
  if (e.timer) { clearTimeout(e.timer); e.timer = null; }
  return e.dirty || e.saving ? save(e) : Promise.resolve();
}

// Changes episode `id` from anywhere: the editor, or work finishing after its page closed.
export function updateEpisode(id: string, fn: (e: TimelineEpisode) => TimelineEpisode, opts?: { merge?: string; quiet?: boolean }) {
  const e = store.entries.get(id);
  if (!e) throw new Error("This episode isn't open.");
  apply(e, fn, opts);
}

// Applies a finished change to episode `id`, loading it first if this tab doesn't have it. `undoable`: the change is a
// step of its own that undo takes back (a new picture), rather than one applied to every kept version (a voice finishing).
export async function applyToEpisode(id: string, fn: (e: TimelineEpisode) => TimelineEpisode, o: { undoable?: boolean } = {}): Promise<void> {
  const e = await entryFor(id);
  apply(e, fn, o.undoable ? {} : { quiet: true });
}

export function undoEpisode(id: string) {
  const e = store.entries.get(id);
  if (!e?.past.length) return;
  e.lastMerge = null;
  e.future = [e.present, ...e.future];
  e.present = e.past[e.past.length - 1]!;
  e.past = e.past.slice(0, -1);
  touched(e);
}
export function redoEpisode(id: string) {
  const e = store.entries.get(id);
  if (!e?.future.length) return;
  e.lastMerge = null;
  e.past = [...e.past, e.present];
  e.present = e.future[0]!;
  e.future = e.future.slice(1);
  touched(e);
}

export function subscribeEpisode(id: string, l: () => void): () => void {
  const e = store.entries.get(id);
  if (!e) return () => {};
  e.listeners.add(l);
  return () => { e.listeners.delete(l); };
}
export const episodeView = (id: string): EpisodeView | null => store.entries.get(id)?.view ?? null;

// The episode was deleted: drop it, and any save still waiting for it.
export function forgetEpisode(id: string) {
  const e = store.entries.get(id);
  if (e?.timer) clearTimeout(e.timer);
  if (e) e.stopped = true;
  store.entries.delete(id);
}
